import { ExtractComponentProp } from '@approvalmax/types';
import { Popup, Text } from '@approvalmax/ui/src/components';
import { memo, useCallback, useMemo, useState } from 'react';
import { noValue } from 'shared/constants';
import { GetQBooksBudgetsImpact, useGetQBooksBudgetsImpact } from 'shared/data/webApp/v2';
import { BudgetsQBooksDimensionType } from 'shared/data/webApp/v2';

import { LineItemsTable } from '../../../components/lineItemsSection/LineItemsTable';
import CollapsibleSection from '../../xero/XeroBudgetingSection/components/CollapsibleSection/CollapsibleSection';
import { QBooksBudgetImpactDetailsPopupContent } from '../QBooksBudgetImpactDetailsPopupContent/QBooksBudgetImpactDetailsPopupContent';
import { getColumnDefinitions } from './QBooksBudgetingSection.config';
import { messages } from './QBooksBudgetingSection.messages';
import { CustomLabels, QBooksBudgetingSectionProps } from './QBooksBudgetingSection.types';

export const QBooksBudgetingSection = memo<QBooksBudgetingSectionProps>((props) => {
    const { company, request } = props;

    const [openedBudgetImpact, setOpenedBudgetImpact] = useState<
        GetQBooksBudgetsImpact['response']['budgetImpacts'][number] | null
    >(null);

    const { data } = useGetQBooksBudgetsImpact(
        {
            path: {
                companyId: company.id,
                requestId: request.id,
            },
        },
        {
            enabled: company.flags.hasActiveIntegration,
        }
    );

    const openBudgetImpactDetailsPopup = useCallback(
        (item: GetQBooksBudgetsImpact['response']['budgetImpacts'][number]) => {
            setOpenedBudgetImpact(item);
        },
        []
    );

    const handleToggleBudgetImpactDetailsPopup = useCallback<ExtractComponentProp<typeof Popup, 'onToggle'>>(
        (isOpen) => {
            if (!isOpen) {
                setOpenedBudgetImpact(null);
            }
        },
        []
    );

    const customLabels = useMemo<CustomLabels>(() => {
        const subdividedByClass = data?.budgetImpacts.find(
            (item) => item.subdividedBy?.type === BudgetsQBooksDimensionType.class
        );
        const subdividedByLocation = data?.budgetImpacts.find(
            (item) => item.subdividedBy?.type === BudgetsQBooksDimensionType.location
        );
        const subdividedByCustomer = data?.budgetImpacts.find(
            (item) => item.subdividedBy?.type === BudgetsQBooksDimensionType.customer
        );

        return {
            class: subdividedByClass?.subdividedBy?.label ?? undefined,
            location: subdividedByLocation?.subdividedBy?.label ?? undefined,
            customer: subdividedByCustomer?.subdividedBy?.label ?? undefined,
        };
    }, [data]);

    const columnDefinitions = useMemo(() => {
        return getColumnDefinitions(data?.currency ?? '', customLabels, openBudgetImpactDetailsPopup);
    }, [data?.currency, customLabels, openBudgetImpactDetailsPopup]);

    if (!data?.budgetImpacts.length) return null;

    return (
        <>
            <CollapsibleSection title={messages.sectionTitle} defaultExpanded>
                <Text font='body' fontSize='small'>
                    {messages.description}
                </Text>

                <LineItemsTable
                    columnDefinitions={columnDefinitions}
                    lineItems={data.budgetImpacts}
                    emptyCellValue={noValue}
                />
            </CollapsibleSection>

            <Popup open={openedBudgetImpact !== null} onToggle={handleToggleBudgetImpactDetailsPopup} size='large'>
                <QBooksBudgetImpactDetailsPopupContent
                    data={openedBudgetImpact}
                    budgetCurrency={data?.currency ?? ''}
                    request={request}
                />
            </Popup>
        </>
    );
});

QBooksBudgetingSection.displayName = 'QBooksBudgetingSection';
